import { Component, DestroyRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { Vehicle, VehiclesResolve } from '@shared/models';
import { ArivoDialogComponent } from '@shared/components/ui-elements';
import { ActivatedRoute } from '@angular/router';
import { formatLicensePlate, showSpinner, TypedRoute } from '@shared/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VehiclesApiService } from './vehicles-api.service';
import { ToastService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { AmplitudeEventService, FeatureFlagService } from '@core/services';
import { ContractEditOpened } from '@integrations/amplitude';

@Component({
  selector: 'app-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrl: './vehicles.component.scss',
})
export class VehiclesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  showShortTermParking: boolean = false;
  vehicles: Vehicle[] = [];

  @ViewChild('deleteVehicleDialog') deleteVehicleDialog!: ArivoDialogComponent;
  deleteVehicleItem?: Vehicle;

  @ViewChild('vehicleDescriptionDialog') vehicleDescriptionDialog!: ArivoDialogComponent;
  descriptionVehicleItem?: Vehicle;
  vehicleDescription: string = '';

  constructor(
    private toastService: ToastService,
    private translateService: TranslateService,
    private vehiclesApiService: VehiclesApiService,
    private amplitudeEventService: AmplitudeEventService,
    private featureFlagService: FeatureFlagService,
    @Inject(ActivatedRoute) private route: TypedRoute<VehiclesResolve>,
  ) {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.vehicles = data.vehiclesApiResponse;
    });
  }

  ngOnInit() {
    this.showShortTermParking = this.featureFlagService.getFeatureFlag('enable_ppu') === true;
  }

  openDeleteVehicleDialog(event: MouseEvent, vehicle: Vehicle): void {
    event.stopPropagation();
    if (vehicle.garage_contracts.length === 0) {
      this.deleteVehicleItem = vehicle;
      this.deleteVehicleDialog.open = true;
    }
  }

  closeDeleteVehicleDialog(): void {
    this.deleteVehicleItem = undefined;
    this.deleteVehicleDialog.open = false;
  }

  openVehicleDescriptionDialog(event: MouseEvent, vehicle: Vehicle): void {
    event.stopPropagation();
    this.descriptionVehicleItem = vehicle;
    this.vehicleDescription = vehicle.description ?? '';
    this.vehicleDescriptionDialog.open = true;
  }

  closeVehicleDescriptionDialog(): void {
    this.descriptionVehicleItem = undefined;
    this.vehicleDescription = '';
    this.vehicleDescriptionDialog.open = false;
  }

  saveVehicleDescription(): void {
    const vehicle = this.descriptionVehicleItem;
    if (!vehicle?.id) {
      return;
    }

    this.vehiclesApiService
      .updateVehicleDescription(vehicle.id, this.vehicleDescription)
      .pipe(showSpinner())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedVehicle) => {
          this.vehicles = this.vehicles.map((item) => (item.id === vehicle.id ? { ...item, description: updatedVehicle.description } : item));
          this.closeVehicleDescriptionDialog();

          this.toastService.add({
            message: this.translateService.instant('Vehicles.UpdateDescriptionSuccess', {
              licensePlate: formatLicensePlate(vehicle.license_plate, vehicle.license_plate_country),
            }),
            type: 'success',
          });
        },
        error: (err) => {
          if (err.status === 400 && err.error?.detail) {
            const detail = err.error.detail;
            for (const key in detail) {
              if (detail.hasOwnProperty(key)) {
                detail[key].forEach((message: string) => {
                  this.toastService.add({
                    message: message,
                    type: 'error',
                  });
                });
              }
            }
          } else {
            this.toastService.add({
              message: this.translateService.instant('Vehicles.UpdateDescriptionFailed', {
                licensePlate: formatLicensePlate(vehicle.license_plate, vehicle.license_plate_country),
              }),
              type: 'error',
            });
          }
        },
      });
  }

  deleteVehicle(): void {
    const vehicle = this.deleteVehicleItem;
    if (vehicle) {
      this.deleteVehicleItem = undefined;
      this.deleteVehicleDialog.open = false;

      this.vehiclesApiService
        .deleteVehicle(vehicle.id!)
        .pipe(showSpinner())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.vehicles = this.vehicles.filter((item) => item.id !== vehicle.id);

            this.toastService.add({
              message: this.translateService.instant('Vehicles.DeleteVehicleSuccess', {
                licensePlate: formatLicensePlate(vehicle.license_plate, vehicle.license_plate_country),
              }),
              type: 'success',
            });
          },
        });
    }
  }

  sendContractEditOpenedEventToAmplitude(): void {
    this.amplitudeEventService.trackEvent(
      new ContractEditOpened({
        referredByPage: 'vehicles',
      }),
    );
  }
}
